import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import { DataGrid, type DataGridProps, type GridColDef } from '@mui/x-data-grid';

type WidthMap = Record<string, number>;
type DensityPrefs = { rowHeightPx: number; rowGapPx: number };

const DEFAULT_ROW_HEIGHT = 44;
const DEFAULT_ROW_GAP = 6;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function readStoredWidths(storageKey?: string): WidthMap {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as WidthMap;
  } catch {
    return {};
  }
}

function storeWidths(storageKey: string, widths: WidthMap) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // ignore
  }
}

function readDensityPrefs(storageKey?: string): DensityPrefs | null {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const rowHeightPx = Number((parsed as any).rowHeightPx);
    const rowGapPx = Number((parsed as any).rowGapPx);
    if (!Number.isFinite(rowHeightPx) || !Number.isFinite(rowGapPx)) return null;
    return { rowHeightPx, rowGapPx };
  } catch {
    return null;
  }
}

function storeDensityPrefs(storageKey: string, prefs: DensityPrefs) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export type ResizableDataGridProps = Omit<DataGridProps, 'columns'> & {
  columns: GridColDef[];
  /**
   * Optional localStorage key to persist column widths per-grid.
   * Example: "gridWidths.requests"
   */
  storageKey?: string;
  /**
   * If true, shows an in-grid UI to change row height + row gap.
   * Settings are persisted per-grid (derived from storageKey).
   */
  enableDensityControls?: boolean;
};

/**
 * Community-safe column resize:
 * MUI X community doesn't expose a built-in "drag resize" API in our current setup,
 * so we implement it via mouse events on the column separator elements.
 */
export const ResizableDataGrid: React.FC<ResizableDataGridProps> = ({ columns, storageKey, sx, ...props }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [widths, setWidths] = useState<WidthMap>(() => readStoredWidths(storageKey));

  // Keep widths in sync if storageKey changes.
  useEffect(() => {
    setWidths(readStoredWidths(storageKey));
  }, [storageKey]);

  const densityStorageKey = storageKey ? `${storageKey}.density` : undefined;
  const [density, setDensity] = useState<DensityPrefs>(() => readDensityPrefs(densityStorageKey) || { rowHeightPx: DEFAULT_ROW_HEIGHT, rowGapPx: DEFAULT_ROW_GAP });

  useEffect(() => {
    setDensity(readDensityPrefs(densityStorageKey) || { rowHeightPx: DEFAULT_ROW_HEIGHT, rowGapPx: DEFAULT_ROW_GAP });
  }, [densityStorageKey]);

  const colByField = useMemo(() => {
    const m = new Map<string, GridColDef>();
    columns.forEach((c) => m.set(String(c.field), c));
    return m;
  }, [columns]);

  const mergedColumns: GridColDef[] = useMemo(() => {
    return columns.map((c) => {
      const field = String(c.field);
      const w = widths[field];
      if (!w) return c;
      return { ...c, width: w, flex: undefined };
    });
  }, [columns, widths]);

  const rowHeightPx = density.rowHeightPx;
  const rowGapPx = density.rowGapPx;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    let activeField: string | null = null;
    let startX = 0;
    let startW = 0;

    const onMouseMove = (e: MouseEvent) => {
      if (!activeField) return;
      const col = colByField.get(activeField);
      const minW = typeof col?.minWidth === 'number' ? col.minWidth : 60;
      const maxW = typeof col?.maxWidth === 'number' ? col.maxWidth : 1200;
      const nextW = clamp(startW + (e.clientX - startX), minW, maxW);
      setWidths((prev) => {
        const next = { ...prev, [activeField as string]: Math.round(nextW) };
        if (storageKey) storeWidths(storageKey, next);
        return next;
      });
    };

    const onMouseUp = () => {
      activeField = null;
      startX = 0;
      startW = 0;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    const onMouseDownCapture = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Only start resizing if the user grabbed the separator.
      const sep = target.closest('.MuiDataGrid-columnSeparator') as HTMLElement | null;
      if (!sep) return;

      const header = sep.closest('.MuiDataGrid-columnHeader') as HTMLElement | null;
      const field = header?.getAttribute('data-field');
      if (!field) return;

      // Don't let header clicks trigger sorting while resizing.
      e.preventDefault();
      e.stopPropagation();

      activeField = String(field);
      startX = e.clientX;

      // Use the actual rendered header width as the starting width.
      startW = Math.round(((header?.getBoundingClientRect()?.width || 0) as number) || 0);
      if (!startW) {
        const fallback = colByField.get(activeField)?.width;
        startW = typeof fallback === 'number' ? fallback : 140;
      }

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    el.addEventListener('mousedown', onMouseDownCapture, true);
    return () => {
      el.removeEventListener('mousedown', onMouseDownCapture, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [colByField, storageKey]);

  const [densityAnchor, setDensityAnchor] = useState<HTMLElement | null>(null);
  const openDensity = Boolean(densityAnchor);
  const enableDensityControls = !!(props as any).enableDensityControls && !!storageKey;
  const overlayPadRight = enableDensityControls ? 88 : 0; // reserve space so the button doesn't cover the last column header/cells

  const applyDensity = (next: Partial<DensityPrefs>) => {
    setDensity((prev) => {
      const merged: DensityPrefs = {
        rowHeightPx: next.rowHeightPx ?? prev.rowHeightPx,
        rowGapPx: next.rowGapPx ?? prev.rowGapPx,
      };
      if (densityStorageKey) storeDensityPrefs(densityStorageKey, merged);
      return merged;
    });
  };

  const resetDensity = () => {
    const next: DensityPrefs = { rowHeightPx: DEFAULT_ROW_HEIGHT, rowGapPx: DEFAULT_ROW_GAP };
    setDensity(next);
    if (densityStorageKey) storeDensityPrefs(densityStorageKey, next);
  };

  return (
    <Box ref={rootRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {enableDensityControls ? (
        <>
          <Tooltip title="Row density (height + spacing)">
            <IconButton
              size="small"
              onClick={(e) => setDensityAnchor(e.currentTarget)}
              aria-label="Row density"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 5,
                backgroundColor: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(17,24,39,0.10)',
                backdropFilter: 'blur(6px)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
              }}
            >
              <ViewCompactIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={openDensity}
            anchorEl={densityAnchor}
            onClose={() => setDensityAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 1.5, width: 320 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Row density</Typography>
              <Typography variant="caption" color="text.secondary">
                This page only (saved locally).
              </Typography>
              <Divider sx={{ my: 1.25 }} />

              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                Row height: {Math.round(rowHeightPx)}px
              </Typography>
              <Slider
                size="small"
                value={rowHeightPx}
                min={28}
                max={76}
                step={1}
                onChange={(_, v) => applyDensity({ rowHeightPx: Number(v) })}
              />

              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                Row gap: {Math.round(rowGapPx)}px
              </Typography>
              <Slider size="small" value={rowGapPx} min={0} max={20} step={1} onChange={(_, v) => applyDensity({ rowGapPx: Number(v) })} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Button size="small" variant="text" onClick={resetDensity}>
                  Reset
                </Button>
                <Button size="small" variant="contained" onClick={() => setDensityAnchor(null)}>
                  Done
                </Button>
              </Box>
            </Box>
          </Popover>
        </>
      ) : null}

      <DataGrid
        {...props}
        columns={mergedColumns}
        rowHeight={rowHeightPx}
        // If supported by the installed @mui/x-data-grid version, this gives real vertical spacing.
        // We keep it guarded via "as any" so TS won't block if the prop isn't in types.
        {...({
          rowSpacingType: 'margin',
          getRowSpacing: () => ({ top: rowGapPx / 2, bottom: rowGapPx / 2 }),
        } as any)}
        sx={[
          {
            // Leave a little room so the overlay button doesn't sit directly on top of the last column.
            '& .MuiDataGrid-columnHeaders': { pr: `${overlayPadRight}px`, boxSizing: 'border-box' },
            '& .MuiDataGrid-virtualScroller': {
              pr: `${overlayPadRight}px`,
              boxSizing: 'border-box',
              // Nice "spaced card row" look even without premium: the grid background shows through gaps.
              backgroundColor: 'rgba(17,24,39,0.02)',
            },

            // Make it obvious and easy to grab the resize handle.
            '& .MuiDataGrid-columnSeparator': {
              opacity: 1,
              color: 'rgba(17,24,39,0.25)',
              cursor: 'col-resize',
              width: 12,
              zIndex: 3,
              touchAction: 'none',
            },
            '& .MuiDataGrid-columnSeparator:hover': { color: 'rgba(59,130,246,0.85)' },
            '& .MuiDataGrid-columnSeparator:active': { color: 'rgba(59,130,246,1.0)' },

            '& .MuiDataGrid-row': {
              backgroundColor: 'background.paper',
              borderRadius: 10,
              overflow: 'hidden',
            },
          },
          sx as any,
        ]}
      />
    </Box>
  );
};


